import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarIcon, Check, Copy } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import React, { useState } from 'react';
import { addSeconds, differenceInSeconds, format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { TFunction } from 'i18next'; // Import TFunction
import { Textarea } from '@/components/ui/textarea'; // Import Textarea
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc'; // Import trpc client
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query'; // Keep queryClient
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// Define the expected output type from the tRPC mutation
type CreateApiKeyOutput = {
    key: string;
    id: string;
};


// Frontend validation schema
// Helper function to validate JSON
const isValidJson = (value: string): boolean => {
  if (!value) return true; // Allow empty string
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

// Define schema using a function to access t
const getCreateApiKeyFormSchema = (t: TFunction<"translation", undefined>) => z.object({
  name: z.string().min(1, { message: t('settings.apiKeys.dialog.create.errors.nameRequired', 'Name is required') }),
  prefix: z.string().optional(),
  metadata: z.string().optional(), // Add metadata field
  neverExpires: z.boolean().default(true),
  expiresAt: z.date().optional(),
}).refine(data => data.neverExpires || data.expiresAt, {
  message: t('settings.apiKeys.dialog.create.errors.expiresRequired', 'Expiration date is required if "Never Expires" is unchecked.'),
  path: ['expiresAt'], // Attach error to the date field
}).refine(data => isValidJson(data.metadata ?? ''), {
  message: t('settings.apiKeys.dialog.create.errors.metadataInvalidJson', 'Metadata must be valid JSON if provided.'),
  path: ['metadata'], // Attach error to the metadata field
});

// Infer type from the schema function's return type
type CreateApiKeyFormValues = z.infer<ReturnType<typeof getCreateApiKeyFormSchema>>;

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateApiKeyDialog({ open, onOpenChange }: CreateApiKeyDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient(); // Get query client instance
  const [showGeneratedKeyDialog, setShowGeneratedKeyDialog] = useState(false);
  const [generatedKeyInfo, setGeneratedKeyInfo] = useState<{ key: string } | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Get the schema with translations
  const schema = getCreateApiKeyFormSchema(t);

  const form = useForm<CreateApiKeyFormValues>({
    resolver: zodResolver(schema), // Use the translated schema
    defaultValues: {
      name: '',
      prefix: '',
      neverExpires: true,
      expiresAt: undefined,
      metadata: '', // Add default value for metadata
    },
  });

  // Use the new tRPC mutation hook
  const createMutation = trpc.settings.createApiKey.useMutation({
    onSuccess: (data: CreateApiKeyOutput) => {
      toast.success(t('settings.apiKeys.createSuccessToast', 'API Key created successfully.'));
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      setGeneratedKeyInfo({ key: data.key });
      setShowGeneratedKeyDialog(true);
      onOpenChange(false);
      form.reset();
    },
    onError: (error) => { // tRPC error type
      toast.error(error.message || t('settings.apiKeys.createErrorToast', 'Failed to create API Key'));
    },
  });


  const onSubmit = (values: CreateApiKeyFormValues) => {
    let expiresIn: number | undefined = undefined;
    let parsedMetadata: Record<string, unknown> | undefined = undefined;

    // Calculate expiresIn
    if (!values.neverExpires && values.expiresAt) {
      expiresIn = differenceInSeconds(values.expiresAt, new Date());
      if (expiresIn <= 0) {
        form.setError('expiresAt', { message: t('settings.apiKeys.dialog.create.errors.expiresInFuture', 'Expiration date must be in the future.') });
        return;
      }
    }

    // Parse metadata - Zod refine should have already validated it
    if (values.metadata) {
      try {
        parsedMetadata = JSON.parse(values.metadata);
      } catch (e) {
        form.setError('metadata', { message: t('settings.apiKeys.dialog.create.errors.metadataParseError', 'Invalid JSON format.') });
        return;
      }
    }

    // Call the new mutate function with the prepared data matching the tRPC input schema
    createMutation.mutate({
      name: values.name,
      prefix: values.prefix || undefined,
      expiresIn: expiresIn,
      metadata: parsedMetadata, // Pass parsed metadata
    });
  };

  const handleCopyKey = async () => {
    if (generatedKeyInfo?.key) {
      try {
        await navigator.clipboard.writeText(generatedKeyInfo.key);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Reset icon after 2 seconds
      } catch (err) {
        toast.error(t('settings.apiKeys.copyErrorToast', 'Failed to copy API key.'));
      }
    }
  };

  const handleGeneratedKeyDialogClose = () => {
    setShowGeneratedKeyDialog(false);
    setGeneratedKeyInfo(null); // Clear the key info
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('settings.apiKeys.createDialogTitle', 'Create API Key')}</DialogTitle>
            <DialogDescription>
              {t('settings.apiKeys.createDialogDescription', 'Enter the details for your new API key.')}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.apiKeys.form.nameLabel', 'Name')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('settings.apiKeys.form.namePlaceholder', 'My Production Key')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="prefix"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.apiKeys.form.prefixLabel', 'Prefix (Optional)')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('settings.apiKeys.form.prefixPlaceholder', 'prod')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="metadata"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.apiKeys.form.metadataLabel', 'Metadata (JSON, Optional)')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('settings.apiKeys.form.metadataPlaceholder', '{\n  "userId": "123",\n  "scope": "read"\n}')}
                        className="min-h-[80px] font-mono text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="neverExpires"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4 bg-muted/50"> {/* Removed border, added subtle background */}
                     <FormControl>
                       <Checkbox
                         checked={field.value}
                         onCheckedChange={(checked) => {
                           field.onChange(checked);
                           if (checked) {
                             form.setValue('expiresAt', undefined); // Clear date if never expires
                             form.clearErrors('expiresAt'); // Clear potential errors
                           }
                         }}
                       />
                     </FormControl>
                     <div className="space-y-1 leading-none">
                       <FormLabel>{t('settings.apiKeys.form.neverExpiresLabel', 'Never Expires')}</FormLabel>
                     </div>
                  </FormItem>
                )}
              />
              {!form.watch('neverExpires') && (
                <FormField
                  control={form.control}
                  name="expiresAt"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('settings.apiKeys.form.expiresAtLabel', 'Expiration Date')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>{t('settings.apiKeys.form.pickDatePlaceholder', 'Pick a date')}</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} // Disable past dates
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <DialogFooter>
                 <DialogClose asChild>
                   <Button type="button" variant="outline">
                     {t('common.cancel', 'Cancel')}
                   </Button>
                 </DialogClose>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? t('common.creating', 'Creating...') : t('common.create', 'Create')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={showGeneratedKeyDialog} onOpenChange={handleGeneratedKeyDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.apiKeys.generatedKeyTitle', 'API Key Generated')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.apiKeys.generatedKeyDescription', 'Please copy your new API key. You won\'t be able to see it again!')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-4 flex items-center space-x-2 rounded-md bg-muted p-3 font-mono text-sm"> {/* Removed border */}
            <span className="flex-1 break-all">{generatedKeyInfo?.key ?? t('settings.apiKeys.dialog.showKey.errorGenerating', 'Error generating key')}</span> {/* Removed truncate, added break-all */}
            <Button variant="ghost" size="icon" onClick={handleCopyKey} className="h-7 w-7">
              {isCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              <span className="sr-only">{isCopied ? t('settings.apiKeys.dialog.showKey.copiedIndicator', 'Copied') : t('settings.apiKeys.dialog.showKey.copyButton', 'Copy Key')}</span>
            </Button>
          </div>
          <AlertDialogFooter className="mt-4">
            <AlertDialogAction onClick={handleGeneratedKeyDialogClose}>{t('common.close', 'Close')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}